/**
 * This file contains authentication helpers.
 */

import { ComponentChildren, createContext } from 'preact';
import { StateUpdater, useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { RuzukuGlobal, Tenant } from 'server/types';
import { RpxResponse, rpx } from '../rpx-client';
import { serialAsync } from 'client/utils/serial-async';
import type { Dispatch } from 'preact/hooks';

export type CurrentUser = NonNullable<RpxResponse<typeof rpx.auth.getCurrentUser>>;
type Metadata = CurrentUser['metadata'];

export const globalConfig = () => (window as any).ruzukuGlobal as RuzukuGlobal;

const store = rpx.auth;

const notImplemented = () => {
  throw new Error('Not implemented');
};

export interface Auth {
  user?: CurrentUser;
  setUser: Dispatch<StateUpdater<CurrentUser | undefined>>;
  config: RuzukuGlobal;
  updateUserMetadata: (metadata: Partial<Metadata>) => Promise<Metadata>;
  login(promise: Promise<CurrentUser>): Promise<CurrentUser>;
  logout(): Promise<void>;
}

export const emptyContext: Auth = {
  logout: notImplemented,
  login: notImplemented,
  updateUserMetadata: notImplemented,
  config: globalConfig(),
  setUser: () => {},
};

const AuthContext = createContext<Auth>(emptyContext);

export function useAuth() {
  return useContext(AuthContext);
}

export function useCurrentUser() {
  return useAuth().user;
}

export function useCurrentTenant(): Tenant {
  return useAuth().config?.tenant;
}

export function useConfiguration() {
  return useAuth()?.config;
}

const logout = serialAsync(store.logout);

export function AuthContextProvider(props: { children: ComponentChildren }) {
  const [user, setUser] = useState<Auth['user']>(() => globalConfig().user);

  // Best-effort, once per login: if the browser's timezone doesn't match
  // what's on file, ask the server to correct it. This is how accounts
  // created without a real detected timezone (e.g. old bulk-invitation
  // signups) self-heal, without a one-off backfill that would have no way
  // to guess anyone's actual zone. A no-op server-side once the person has
  // set their timezone themselves. Never blocks or surfaces an error.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      return;
    }
    let detected: string;
    try {
      detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return;
    }
    if (!detected || detected === user?.timezone) {
      return;
    }
    store
      .syncDetectedTimezone({ timezone: detected })
      .then(({ timezone }) => {
        setUser((u) => (u && u.timezone !== timezone ? { ...u, timezone } : u));
      })
      .catch(() => {
        // Best-effort only — the standup/scheduling logic still falls back
        // to whatever is already on file.
      });
    // Only re-run when the logged-in account changes, not on every render
    // that happens to produce a new `user` object (e.g. metadata updates).
  }, [userId]);

  const ctx = useMemo<Auth>(() => {
    return {
      user,
      setUser,
      config: globalConfig(),
      async login(p) {
        const newUser = await p;
        setUser(newUser);
        ctx.user = newUser;
        return newUser;
      },
      async logout() {
        await logout();
        setUser(undefined);
        localStorage.removeItem('last-root-page-url');
        // Dispatch a logout event so any components that care about this can
        // react accordingly.
        document.dispatchEvent(new Event('ruz:logout'));
      },
      async updateUserMetadata(metadata: Partial<Metadata>) {
        const newMetadata = await store.updateMetadata(metadata);
        setUser((u) => {
          if (!u) {
            return u;
          }
          return {
            ...u,
            metadata: newMetadata,
          };
        });
        return newMetadata;
      },
    };
  }, [setUser, user]);

  return <AuthContext.Provider value={ctx}>{props.children}</AuthContext.Provider>;
}
